import { useState, useEffect, useRef } from "react";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Paperclip, FileText, X, Loader2, Camera, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { compute } from "../lib/gst";
import { money, TREATMENTS, PAYMENT_METHODS } from "../lib/format";

const empty = (type) => ({
  type, date: new Date().toISOString().slice(0, 10), description: "", party: "",
  category: "", treatment: "standard", amount: "", is_capital: false, has_tax_invoice: true,
  is_wages: false, payg_withheld: 0, super_amount: 0, attachment_path: "", attachment_name: "",
  payment_method: "",
  is_personal: false, gst_claimable: true, tax_deductible: true, deductible_percent: 100, project_id: "", account_id: "",
});

export default function TransactionDialog({ open, onOpenChange, type, categories, onSave, editing }) {
  const [form, setForm] = useState(empty(type));
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const fileRef = useRef();
  const scanRef = useRef();

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/bank-accounts").then((r) => setAccounts(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (editing) setForm({ ...editing, amount: String(editing.amount) });
    else setForm(empty(type));
  }, [editing, type, open]);

  const cats = categories.filter((c) => c.type === type);
  const amount = parseFloat(form.amount) || 0;
  const { gst, net } = compute(amount, form.treatment);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachment_path: data.path, attachment_name: data.name }));
      toast.success("Receipt attached");
    } catch {
      toast.error("Could not upload file");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const scanReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/receipts/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        amount: data.amount ? String(data.amount) : f.amount,
        date: data.date || f.date,
        description: data.description || f.description,
        treatment: data.treatment || f.treatment,
        attachment_path: data.attachment_path || f.attachment_path,
        attachment_name: data.attachment_name || f.attachment_name,
      }));
      toast.success("Receipt scanned — check the details");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read the receipt");
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  };

  const submit = () => {
    if (!form.description || !amount) return;
    onSave({ ...form, amount });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="transaction-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editing ? "Edit" : "New"} {type === "income" ? "Income" : "Expense"}
          </DialogTitle>
          <DialogDescription>
            Enter the GST-inclusive total — GST is calculated automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5 py-1">
          <input ref={scanRef} type="file" accept="image/*" capture="environment" onChange={scanReceipt} className="hidden" data-testid="txn-scan-input" />
          <button type="button" onClick={() => scanRef.current?.click()} disabled={scanning} data-testid="txn-scan-btn"
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary/5 border border-primary/20 text-primary px-3 py-2.5 text-sm font-medium hover:bg-primary/10 transition-colors">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            {scanning ? "Reading receipt…" : "Scan a receipt photo to auto-fill"}
          </button>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" data-testid="txn-date" value={form.date}
                onChange={(e) => set("date", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label>Amount (incl. GST)</Label>
              <Input type="number" step="0.01" data-testid="txn-amount" value={form.amount}
                onChange={(e) => set("amount", e.target.value)} className="mt-1.5" placeholder="0.00" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input data-testid="txn-description" value={form.description}
              onChange={(e) => set("description", e.target.value)} className="mt-1.5"
              placeholder={type === "income" ? "Invoice / sale" : "What was purchased"} />
          </div>
          <div>
            <Label>{type === "income" ? "Customer" : "Supplier"}</Label>
            <Input data-testid="txn-party" value={form.party}
              onChange={(e) => set("party", e.target.value)} className="mt-1.5" placeholder="Name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => {
                set("category", v);
                const c = cats.find((x) => x.name === v);
                if (c) set("treatment", c.treatment);
              }}>
                <SelectTrigger className="mt-1.5" data-testid="txn-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GST treatment</Label>
              <Select value={form.treatment} onValueChange={(v) => set("treatment", v)}>
                <SelectTrigger className="mt-1.5" data-testid="txn-treatment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TREATMENTS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Payment method <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Select value={form.payment_method || "none"} onValueChange={(v) => set("payment_method", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="txn-payment-method"><SelectValue placeholder="How was it paid?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Project <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Select value={form.project_id || "none"} onValueChange={(v) => set("project_id", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="txn-project"><SelectValue placeholder="No project" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Bank account <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Select value={form.account_id || "none"} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="txn-account"><SelectValue placeholder="Not set" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not set</SelectItem>
                {accounts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}{a.provider ? ` · ${a.provider}` : ""}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {type === "expense" && (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Personal / drawings</div>
                <div className="text-xs text-muted-foreground">Paid from the business for private use</div>
              </div>
              <Switch checked={form.is_personal} onCheckedChange={(v) => set("is_personal", v)} data-testid="txn-personal" />
            </div>
          )}
          {type === "expense" && !form.is_personal && (
            <div className="rounded-lg border border-border px-3 py-2.5 space-y-3" data-testid="txn-claimable-block">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Claim GST credit</div>
                <Switch checked={form.gst_claimable} onCheckedChange={(v) => set("gst_claimable", v)} data-testid="txn-gst-claimable" />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Tax deductible</div>
                <Switch checked={form.tax_deductible} onCheckedChange={(v) => set("tax_deductible", v)} data-testid="txn-tax-deductible" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Business-use %</Label>
                <Input type="number" min="0" max="100" step="1" value={form.deductible_percent}
                  onChange={(e) => set("deductible_percent", parseFloat(e.target.value) || 0)} className="mt-1.5" data-testid="txn-deductible-percent" />
              </div>
            </div>
          )}

          {type === "expense" && (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Capital purchase (G10)</div>
                <div className="text-xs text-muted-foreground">Assets & equipment</div>
              </div>
              <Switch checked={form.is_capital} onCheckedChange={(v) => set("is_capital", v)} data-testid="txn-capital" />
            </div>
          )}
          {type === "expense" && amount >= 82.5 && (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Tax invoice held</div>
                <div className="text-xs text-muted-foreground">Required for purchases ≥ $82.50</div>
              </div>
              <Switch checked={form.has_tax_invoice} onCheckedChange={(v) => set("has_tax_invoice", v)} data-testid="txn-invoice" />
            </div>
          )}

          {type === "expense" && (
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Wages payment (W1)</div>
                <div className="text-xs text-muted-foreground">Salary/wages for BAS</div>
              </div>
              <Switch checked={form.is_wages} onCheckedChange={(v) => { set("is_wages", v); if (v) set("treatment", "gst_free"); }} data-testid="txn-wages" />
            </div>
          )}
          {type === "expense" && form.is_wages && (
            <div>
              <Label>PAYG withheld (W2)</Label>
              <Input type="number" step="0.01" data-testid="txn-payg" value={form.payg_withheld}
                onChange={(e) => set("payg_withheld", parseFloat(e.target.value) || 0)} className="mt-1.5" placeholder="0.00" />
            </div>
          )}
          {type === "expense" && form.is_wages && (
            <div>
              <div className="flex items-center justify-between">
                <Label>Superannuation (SG)</Label>
                <button type="button" onClick={() => set("super_amount", Math.round(net * 0.12 * 100) / 100)}
                  data-testid="txn-super-auto" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
                  <Sparkles className="w-3 h-3" /> Use 12% ({money(Math.round(net * 0.12 * 100) / 100)})
                </button>
              </div>
              <Input type="number" step="0.01" data-testid="txn-super" value={form.super_amount}
                onChange={(e) => set("super_amount", parseFloat(e.target.value) || 0)} className="mt-1.5" placeholder="0.00" />
            </div>
          )}

          <div>
            <Label>Receipt / attachment</Label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={uploadReceipt} className="hidden" data-testid="txn-receipt-input" />
            {form.attachment_path ? (
              <div className="mt-1.5 flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid="txn-receipt-attached">
                <a href={fileUrl(form.attachment_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary truncate hover:underline">
                  <FileText className="w-4 h-4 shrink-0" /> <span className="truncate">{form.attachment_name || "View receipt"}</span>
                </a>
                <button type="button" onClick={() => setForm((f) => ({ ...f, attachment_path: "", attachment_name: "" }))}
                  className="p-1 text-muted-foreground hover:text-destructive transition-colors"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="txn-receipt-btn"
                className="mt-1.5 w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Attach photo or PDF"}
              </button>
            )}
          </div>

          <div className="rounded-lg bg-secondary px-4 py-3 flex justify-between text-sm">
            <div>
              <div className="text-muted-foreground text-xs">Net (ex GST)</div>
              <div className="num font-semibold">{money(net)}</div>
            </div>
            <div className="text-right">
              <div className="text-muted-foreground text-xs">GST</div>
              <div className="num font-semibold text-sky-700">{money(gst)}</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="txn-cancel">Cancel</Button>
          <Button onClick={submit} data-testid="txn-save">{editing ? "Save changes" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
